import {
  createSealedVisualizationProof,
  isVisualizationRecordEligibleForInstance,
  SealedVisualizationProof,
  VisualizationCounter,
  VisualizationProofOutboxState,
  VisualizationProofStore,
} from './visualization-proof-outbox.types';

export const DEFAULT_MAX_MEMORY_OUTBOX_RECORDS = 1000;

export class MemoryVisualizationProofStore implements VisualizationProofStore {
  readonly kind = 'memory' as const;

  private readonly counters = new Map<string, VisualizationCounter>();
  private readonly proofs = new Map<string, SealedVisualizationProof>();

  constructor(private readonly maxRecords: number = DEFAULT_MAX_MEMORY_OUTBOX_RECORDS) {}

  async incrementCounter(counter: VisualizationCounter): Promise<void> {
    const current = this.counters.get(counter.key);

    if (current) {
      this.counters.set(counter.key, {
        ...current,
        identifier: counter.identifier,
        visualizations: current.visualizations + counter.visualizations,
        updatedAt: counter.updatedAt,
      });
      return;
    }

    this.ensureCapacity();
    this.counters.set(counter.key, { ...counter });
  }

  async importState(state: VisualizationProofOutboxState): Promise<void> {
    for (const proof of state.proofs) {
      if (this.proofs.has(proof.proofId)) {
        continue;
      }

      this.ensureCapacity();
      this.proofs.set(proof.proofId, { ...proof });
    }

    for (const counter of state.counters) {
      await this.incrementCounter(counter);
    }
  }

  async exportState(): Promise<VisualizationProofOutboxState> {
    return {
      counters: Array.from(this.counters.values(), (counter) => ({ ...counter })),
      proofs: Array.from(this.proofs.values(), (proof) => ({ ...proof })),
    };
  }

  async sealCounters(limit: number, instanceId?: string): Promise<void> {
    const sealLimit = Math.min(
      Math.max(0, Math.floor(limit)),
      Math.max(0, this.maxRecords - this.proofs.size),
    );
    let sealedCounters = 0;

    for (const counter of this.counters.values()) {
      if (
        sealedCounters >= sealLimit ||
        !isVisualizationRecordEligibleForInstance(counter, instanceId)
      ) {
        continue;
      }

      const proof = createSealedVisualizationProof(counter);
      this.proofs.set(proof.proofId, proof);
      this.counters.delete(counter.key);
      sealedCounters += 1;
    }
  }

  async listProofs(limit: number, instanceId?: string): Promise<SealedVisualizationProof[]> {
    return Array.from(this.proofs.values())
      .filter((proof) => isVisualizationRecordEligibleForInstance(proof, instanceId))
      .slice(0, Math.max(0, limit))
      .map((proof) => ({ ...proof }));
  }

  async deleteProofs(proofIds: string[]): Promise<void> {
    for (const proofId of proofIds) {
      this.proofs.delete(proofId);
    }
  }

  async clear(): Promise<void> {
    this.counters.clear();
    this.proofs.clear();
  }

  close(): void {}

  private ensureCapacity(): void {
    if (this.counters.size + this.proofs.size < this.maxRecords) {
      return;
    }

    const oldestCounterKey = this.counters.keys().next().value as string | undefined;
    if (oldestCounterKey) {
      this.counters.delete(oldestCounterKey);
      return;
    }

    const oldestProofId = this.proofs.keys().next().value as string | undefined;
    if (oldestProofId) {
      this.proofs.delete(oldestProofId);
    }
  }
}
